import type { Vec2 } from '../../../geo/types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import { obbFromFrontEdge } from '../lotObb';
import type { LotPlacer } from '../lotPlacer';
import type { MeasuredLine } from '../measuredLine';
import type { HouseReading, SurveyedRow } from './rowTypes';

interface Span {
  readonly from: number;
  readonly to: number;
}
export type FrontProbe = (a: Vec2, b: Vec2, depth: number, facing: Vec2) => boolean;
export interface ReadFront extends Span {
  readonly house: HouseReading;
}

/** Crossing lanes and precincts split rows before house widths are allocated. */
function frontSpans(
  row: SurveyedRow,
  line: MeasuredLine,
  width: number,
  buildable: PolygonIndex,
  placer: LotPlacer,
  probe?: FrontProbe
): Span[] {
  const sign = Math.sign(row.to - row.from),
    length = Math.abs(row.to - row.from);
  const open: Span[] = [];
  let start: number | null = null;
  for (let at = 0; at <= length + 0.25; at += 0.25) {
    const s = row.from + sign * Math.min(at, length);
    const { point, tangent } = line.pointAt(s);
    const inward: Vec2 =
      row.side === 'left' ? [-tangent[1], tangent[0]] : [tangent[1], -tangent[0]];
    const front: Vec2 = [
      point[0] + inward[0] * (width / 2 + (row.frontOffset ?? 0.07)),
      point[1] + inward[1] * (width / 2 + (row.frontOffset ?? 0.07)),
    ];
    const a: Vec2 = [front[0] - tangent[0] * 0.15, front[1] - tangent[1] * 0.15];
    const b: Vec2 = [front[0] + tangent[0] * 0.15, front[1] + tangent[1] * 0.15];
    const obb = obbFromFrontEdge(a, b, 6, [-inward[0], -inward[1]]);
    const dry =
      at <= length &&
      (probe
        ? probe(a, b, 6, [-inward[0], -inward[1]])
        : buildable.obbInside(obb) && placer.fits(obb));
    if (dry && start === null) start = at;
    if (!dry && start !== null) {
      if (at - start >= 4)
        open.push({ from: row.from + sign * (start + 0.2), to: row.from + sign * (at - 0.45) });
      start = null;
    }
  }
  return open;
}

/** Preserve the recorded order and count while distributing the row over genuine frontages. */
export function readFronts(
  row: SurveyedRow,
  line: MeasuredLine,
  width: number,
  buildable: PolygonIndex,
  placer: LotPlacer,
  probe?: FrontProbe
): ReadFront[] {
  const spans = frontSpans(row, line, width, buildable, placer, probe);
  const lengths = spans.map((s) => Math.abs(s.to - s.from));
  const length = lengths.reduce((a, b) => a + b, 0);
  if (length === 0) return [];
  const shares = lengths.map((l) => (row.houses.length * l) / length);
  const counts = shares.map((s, i) => Math.min(Math.floor(s), Math.floor(lengths[i]! / 3.5)));
  const rank = counts
    .map((_, i) => i)
    .sort((a, b) => shares[b]! - counts[b]! - (shares[a]! - counts[a]!));
  let remaining = row.houses.length - counts.reduce((a, b) => a + b, 0);
  while (remaining > 0) {
    const available = rank.filter((i) => counts[i]! < Math.floor(lengths[i]! / 3.5));
    if (available.length === 0) break;
    for (const i of available) {
      counts[i]!++;
      if (--remaining === 0) break;
    }
  }
  let next = 0;
  return spans.flatMap((span, i) => {
    const houses = row.houses.slice(next, next + counts[i]!);
    next += counts[i]!;
    const weight = houses.reduce((sum, h) => sum + h.widthWeight, 0);
    let along = span.from;
    return houses.map((house) => {
      const to = along + ((span.to - span.from) * house.widthWeight) / weight;
      const front = { from: along, to, house };
      along = to;
      return front;
    });
  });
}
